// ibook-ui/src/components/DialogAccountingForm/DialogAccountingForm.tsx
import { Avatar, Box, Button, DialogActions, DialogContent, DialogTitle, Grid, InputAdornment, MenuItem, Radio, RadioProps, Select, SelectChangeEvent, TextField, Typography, styled } from '@mui/material';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector, } from "../../store/hooks";

import CustomTextField from '../FormFields/CustomTextField';
import { updateAccountingData } from '../../store/accountThunk';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import LoadingButton from '@mui/lab/LoadingButton';

import { ApiStatus } from "../../utils/api.types";
import { StyledInput } from '../Dialog/Dialog.styles';
import { makeStyles } from '@material-ui/core';

const style = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  transition: '0ms',
  width: 650,
  borderRadius: 2,
  bgcolor: 'background.paper',
  border: 'none',
  boxShadow: 24,
  padding: 0
};

const useStyles = makeStyles({
  paper: {
    border: "1.5px solid #e9ecf1",
    width: '108%',
    boxShadow: 'none',
    marginLeft: '-6px',
    borderRadius: 7
  }
});

interface DialogAccountingFormProps {
  open: boolean;
  data: any;
  onClose: (k: any) => void;
}

const DialogAccountingForm: FC<DialogAccountingFormProps> = ({ onClose, open, data }) => {

  const dispatch = useAppDispatch();

  const { accountDetails, status } = useAppSelector(state => state.account);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [image, setImage] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const formRef = useRef<any>();

  // Schema to validate account form.
  const schema = yup.object().shape({
    upiId: yup.string().required("UPI ID is required."),
    gPayPhone: yup.string().required("GPay Number is required."),
  });

  const methods = useForm<any>({ mode: 'onTouched', resolver: yupResolver(schema) });

  const handleSelectFile = (event: any) => {
    const target = event.target as HTMLInputElement;

    if (target.files && target.files[0]) {
      setSelectedFile(target.files[0]);
      setImage(URL.createObjectURL(target.files[0]));
    }
  };

  const innerSubmit = async (data: any) => {
    const form = new FormData();
    form.append('upiId', data.upiId);
    form.append('gPayPhone', data.gPayPhone);

    if (selectedFile) {
      form.append('qrCodeUrl', selectedFile);
    }
    onSubmit(form);
  };

  const onSubmit = (e: any) => {
    dispatch(updateAccountingData(
      {
        accountingPayload: e,
        id: accountDetails._id
      }));
    setFormSubmitted(true);
  }

  const classes = useStyles();

  useEffect(() => {
    const setInitialData = () => {
      if (data) {
        methods.setValue('upiId', data.upiId);
        methods.setValue('gPayPhone', data.gPayPhone);
        setImage(data.qrCodeUrl);
      }
    }
    setInitialData();
  }, [data, methods]);

  useEffect(() => {
    if (status === ApiStatus.SUCCESS && formSubmitted) {
      onClose(false);
      setFormSubmitted(false);
    }
  }, [status, formSubmitted]);

  return (
    <FormProvider {...methods}>
      <Box sx={style} component="form" onSubmit={methods.handleSubmit(data => innerSubmit(data))} ref={formRef} noValidate>
        
        <Grid container >
          <Grid {...{ xs: 12, sm: 8, md: 4, lg: 12, p: 2, pl: 2, bgcolor: '#e9ecf1' }} style={{ borderTopLeftRadius: 8, borderTopRightRadius: 8, height: 50 }} >
            <div style={{ float: 'right', marginTop: -7, marginRight: -5 }}>
              <LoadingButton
                type="submit"
                name={'submit'}
                // disabled={!methods.formState.isValid}
                loading={status === ApiStatus.LOADING}
                style={{ color: '#488dff', backgroundColor: '#fff', border: '1px solid #488dff', marginRight: 10 }}
                variant="contained"
                size='small'>
                {`Save`}
              </LoadingButton>

              <Button
                style={{ color: '#488dff', backgroundColor: '#fff', border: '1px solid #488dff', marginRight: 10 }}
                variant="contained"
                size='small'
                onClick={() => {
                  methods.reset();
                  setImage("");
                  setSelectedFile(null);
                  onClose(false);
                }} >
                Cancel
              </Button>
            </div>
            <h3 style={{ marginTop: -2 }}>Payment Update</h3>
          </Grid>
        </Grid>

        <Grid container p={2} spacing={2}>
          {/* Left Column - UPI ID and GPay Number */}
          <Grid item xs={6}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>UPI ID</h5>
                <TextField placeholder="Enter UPI ID" required size="small"
                  {...methods.register('upiId')}
                  name="upiId"
                  className='c-text-field-4'
                  variant="standard"
                  sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
                  InputProps={{
                    disableUnderline: true,
                  }}
                />
                <p style={{ color: "#c33b3b" }}>{methods.formState.errors.upiId?.message ?? ""}</p>
              </Grid>

              <Grid item xs={12}>
                <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>GPay Number</h5>
                <TextField placeholder="Enter GPay Number" required size="small" {...methods.register('gPayPhone')}
                  className='c-text-field-4'
                  variant="standard"
                  type="number"
                  sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
                  InputProps={{
                    disableUnderline: true,
                  }}
                />
                <p style={{ color: "#c33b3b" }}>{methods.formState.errors.gPayPhone?.message ?? ""}</p>
              </Grid>
            </Grid>
          </Grid>

          {/* Right Column - QR Code */}
          <Grid item xs={6}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>QR Code</h5>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mt: 1,
                border: '1.5px solid #e9ecf1',
                borderRadius: '7px',
                padding: 2,
                backgroundColor: '#fafbfc',
                height: 'fit-content'
              }}
            >
              {image && (
                <Avatar
                  alt="QR Code"
                  imgProps={{ referrerPolicy: "no-referrer" }}
                  src={image}
                  variant='square'
                  sx={{
                    width: 150,
                    height: 150,
                    mb: 2,
                    objectFit: "contain",
                    border: '1px solid #e0e0e0',
                    borderRadius: 1
                  }}
                />
              )}

              <label htmlFor="qrCodeUrl">
                <StyledInput
                  accept="image/*"
                  id="qrCodeUrl"
                  type="file"
                  onChange={handleSelectFile}
                />
                <Button
                  variant="outlined"
                  component="span"
                  size="small"
                  sx={{
                    textTransform: 'none',
                    borderRadius: 1,
                    color: '#488dff',
                    borderColor: '#488dff',
                    backgroundColor: '#fff',
                    '&:hover': {
                      backgroundColor: '#f5f8ff',
                      borderColor: '#488dff'
                    }
                  }}
                >
                  Upload QR Code
                </Button>
              </label>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </FormProvider>
  );
};

export default DialogAccountingForm;